/**
 * @file wq-search-enhance.js
 * @description Enhances the Horizon theme search with WQ-specific
 *   placeholder text and predictive search behavior.
 *   Does NOT modify core Horizon files (RULE-01 compliant).
 * @sprint Design System | Phase 2.6
 */
window.WQ = window.WQ || {};

window.WQ.SearchEnhance = (() => {
  'use strict';

  const WQ_PLACEHOLDER = 'Search by SKU, part number, wavelength\u2026';

  /**
   * Override search input placeholders throughout the page.
   * Targets both the header search and the search modal dialog.
   */
  function updatePlaceholders() {
    const selectors = [
      'search-modal input[type="search"]',
      'search-modal input[type="text"]',
      '.header [type="search"]',
      '.search-page__header input[type="search"]',
      '.search-page__header input[type="text"]',
      'input[name="q"][type="search"]'
    ];

    selectors.forEach(function (sel) {
      var inputs = document.querySelectorAll(sel);
      inputs.forEach(function (input) {
        if (input.placeholder !== WQ_PLACEHOLDER) {
          input.placeholder = WQ_PLACEHOLDER;
        }
      });
    });
  }

  /**
   * Initialize: run on DOMContentLoaded + observe for dynamically
   * inserted search modals (Horizon loads these lazily).
   */
  function init() {
    updatePlaceholders();

    // Observe body for dynamic search modal insertion
    var observer = new MutationObserver(function () {
      updatePlaceholders();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also update on any dialog open event
    document.addEventListener('click', function (e) {
      if (e.target.closest('search-button, .search-action')) {
        // Small delay to let the modal render
        setTimeout(updatePlaceholders, 100);
        setTimeout(updatePlaceholders, 300);
      }
    });
  }

  /* ── Calculator / Article search redirect ── */
  const CALC_KEYWORDS = ['calculator','calc','dispersion','gdd','shg','tbp',
    'sellmeier','grating angle','phase matching','pulse stretching','beam',
    'gaussian','watt dbm','nonlinear','opa wavelength','b-integral',
    'fresnel','pockels','thermal lens','resonator','etalon','prism pair',
    'fourier','diffraction limit','refractive index'];
  const ARTICLE_KEYWORDS = ['tutorial','guide','how to','laser technology',
    'optics skills','eli series','damage threshold','chirped mirror',
    'autocorrelator','dazzler','compressor','pulse broadening'];

  function injectSearchRedirects() {
    if (!window.location.pathname.startsWith('/search')) return;
    const params = new URLSearchParams(window.location.search);
    const q = (params.get('q') || '').toLowerCase().trim();
    if (!q) return;

    const isCalc = CALC_KEYWORDS.some(k => q.includes(k));
    const isArticle = ARTICLE_KEYWORDS.some(k => q.includes(k));

    if (!isCalc && !isArticle) return;

    /* Build banner */
    const banner = document.createElement('div');
    banner.style.cssText = 'max-width:1200px;margin:20px auto;padding:16px 24px;'
      + 'background:#f0f7ff;border:1px solid #bfdbfe;border-radius:8px;'
      + 'display:flex;align-items:center;gap:16px;flex-wrap:wrap;'
      + 'font-family:var(--wq-font);';

    let html = '';
    if (isCalc) {
      html += '<div style="flex:1;min-width:200px">'
        + '<strong style="font-size:16px;color:#111">Looking for a scientific calculator?</strong>'
        + '<p style="margin:4px 0 0;color:#333;font-size:14px">We have 49 free interactive photonics calculators — dispersion, phase matching, beam propagation, and more.</p>'
        + '</div>'
        + '<a href="/pages/calculators" style="display:inline-flex;align-items:center;gap:6px;'
        + 'padding:10px 20px;background:#005CB9;color:#fff;border-radius:6px;'
        + 'text-decoration:none;font-weight:600;font-size:14px;white-space:nowrap">'
        + 'Open Calculator Hub &rarr;</a>';
    }
    if (isArticle && !isCalc) {
      html += '<div style="flex:1;min-width:200px">'
        + '<strong style="font-size:16px;color:#111">Looking for technical resources?</strong>'
        + '<p style="margin:4px 0 0;color:#333;font-size:14px">Browse our tutorials, application notes, and white papers.</p>'
        + '</div>'
        + '<a href="/blogs/resources" style="display:inline-flex;align-items:center;gap:6px;'
        + 'padding:10px 20px;background:#005CB9;color:#fff;border-radius:6px;'
        + 'text-decoration:none;font-weight:600;font-size:14px;white-space:nowrap">'
        + 'Browse Resources &rarr;</a>';
    }
    if (isCalc && isArticle) {
      html += '<a href="/blogs/resources" style="display:inline-flex;align-items:center;gap:6px;'
        + 'padding:10px 20px;background:#fff;color:#005CB9;border:1px solid #005CB9;border-radius:6px;'
        + 'text-decoration:none;font-weight:600;font-size:14px;white-space:nowrap">'
        + 'Browse Articles &rarr;</a>';
    }
    banner.innerHTML = html;

    /* Also show matching articles from Shopify search API */
    fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=article&resources[limit]=6')
      .then(r => r.json())
      .then(data => {
        const articles = (data.resources && data.resources.results && data.resources.results.articles) || [];
        if (articles.length > 0) {
          const grid = document.createElement('div');
          grid.style.cssText = 'max-width:1200px;margin:16px auto;display:grid;'
            + 'grid-template-columns:repeat(auto-fill,minmax(340px,1fr));gap:12px;'
            + 'font-family:var(--wq-font);';
          grid.innerHTML = '<h3 style="grid-column:1/-1;font-size:18px;color:#111;margin:0 0 4px;'
            + 'font-weight:700">Related Articles &amp; Calculators</h3>';
          articles.forEach(a => {
            const img = a.image ? '<img src="' + a.image + '" style="width:80px;height:60px;object-fit:cover;border-radius:4px;flex-shrink:0" loading="lazy">' : '';
            grid.innerHTML += '<a href="' + a.url + '" style="display:flex;gap:12px;align-items:center;'
              + 'padding:12px;background:#fff;border:1px solid #e8e8e8;border-radius:8px;'
              + 'text-decoration:none;color:#111;transition:border-color .15s"'
              + ' onmouseover="this.style.borderColor=\'#005CB9\'" onmouseout="this.style.borderColor=\'#e8e8e8\'">'
              + img
              + '<div><div style="font-weight:600;font-size:14px;line-height:1.3">' + a.title + '</div></div></a>';
          });
          banner.parentElement.insertBefore(grid, banner.nextSibling);
        }
      })
      .catch(() => {});

    /* Insert banner into search results page */
    const target = document.querySelector('.collection') || document.querySelector('main') || document.querySelector('.content-for-layout');
    if (target) {
      target.insertBefore(banner, target.firstChild);
    }
  }

  // Auto-init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { init(); injectSearchRedirects(); });
  } else {
    init();
    injectSearchRedirects();
  }

  return { updatePlaceholders: updatePlaceholders };
})();
